// Cards.jsx - The card that goes inside a display mailer on a shop counter,
// printed four to a sheet of Letter paper, front and back.
//
// The card exists because a display mailer is taken by somebody we know nothing
// about. Until it comes back and is opened there is no name, no address to pay
// to, no consent and no way to tell whose gold is on the bench. The front is the
// form that travels with the items; the back is a QR code that lets somebody do
// the same thing on their phone before they post, which is better for everyone:
// we know it is coming, they get a tracking link, and their consent is recorded
// with a timestamp rather than a tick somebody has to read off paper.
//
// Two pages and one dialog. Page one is four fronts, page two is four backs, and
// the browser is left to do duplex and copies because it is better at both than
// anything that could be built here. Every card is identical, so the two pages
// need no alignment: four identical backs sit correctly behind four identical
// fronts whichever way the printer flips the sheet.
//
// LEGEND
//   1. Rule/Tick     the bits of a paper form, at a size a biro fits in
//   2. CardFront     the form somebody fills in by hand
//   3. CardBack      the QR code, and what it is for
//   4. Cards         the screen: check it, then print

import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router";

import { useAsync } from "../lib/hooks.js";
import { affiliatesApi } from "../lib/api.js";
import { money } from "../lib/format.js";
import { Button, Panel, Alert, Loading } from "../ui/kit.jsx";
import { Printer, ArrowLeft } from "../ui/icons.jsx";

////////// 1. THE BITS OF A PAPER FORM //////////
// A ruled line somebody writes on. The label sits above rather than beside it,
// because beside it the writing space is whatever is left over, and what is left
// over on a quarter sheet is not enough for an address.

const Rule = ({ label, className = "" }) => (
  <label className={`card-rule ${className}`}>
    <span>{label}</span>
    <i />
  </label>
);

// An unticked box. Both of these are unticked and neither is pre-printed with a
// mark: a tick the customer did not make is an agreement we cannot prove, and
// that rule does not stop applying because the form is paper.
const Tick = ({ children }) => (
  <span className="card-tick"><i /><span>{children}</span></span>
);

////////// 2. THE FRONT //////////

function CardFront({ company, coverage }) {
  return (
    <article className="card-face card-front">
      <header className="card-head">
        <span className="card-mark">International <b>Gold</b> &amp; Silver</span>
      </header>

      <p className="card-lede">
        Fill this in and put it in the mailer with your items. Please print clearly.
      </p>

      <div className="card-grid">
        <Rule label="Name" className="card-span" />
        <Rule label="Street address" className="card-span" />
        <Rule label="City" />
        <Rule label="State" />
        <Rule label="ZIP" />
        <Rule label="Phone" />
        <Rule label="Email" className="card-span-2" />
        <Rule label="What you are sending" className="card-span" />
        <Rule label="" className="card-span" />
        <Rule label="" className="card-span" />
      </div>

      {/* Cover is the one thing on this card that costs money to get wrong, so
          it is the one thing in a box. An approval cannot be backdated to cover
          a parcel that is already in the post, which is why the instruction is
          to ring first rather than to write a number here. */}
      <p className="card-cover">
        <b>Covered to {money(coverage)}.</b> If your items are worth more than that,
        call {company.phone} <b>before you post</b>. We cannot add cover to a parcel
        that has already gone.
      </p>

      <div className="card-consent">
        <Tick>
          I have read and accept the terms at {company.site}/legal/terms, and everything
          here is mine to sell.
        </Tick>
        <Tick>Text me about my order. Optional, and you can reply STOP at any time.</Tick>
      </div>

      <div className="card-sign">
        <Rule label="Signature" />
        <Rule label="Date" />
      </div>
    </article>
  );
}

////////// 3. THE BACK //////////
// The QR is the whole point of this side. Everything else on it is there to make
// somebody scan it, and the address is printed underneath in words because a
// phone that will not focus is common and a dead end is not.

function CardBack({ run, company }) {
  return (
    <article className="card-face card-back">
      <div className="card-back-top">
        <span className="card-mark">International <b>Gold</b> &amp; Silver</span>
        <p className="card-back-line">Paid for what it is really worth, since 2008.</p>
      </div>

      <div className="card-qr-wrap">
        <svg
          className="card-qr"
          viewBox={`0 0 ${run.qr.size} ${run.qr.size}`}
          shapeRendering="crispEdges"
          aria-hidden="true"
        >
          <rect width={run.qr.size} height={run.qr.size} fill="#fff" />
          <path d={run.qr.path} fill="#000" />
        </svg>
        <p className="card-qr-say">Scan before you post</p>
      </div>

      <p className="card-back-why">
        Tell us it is coming and we will text you when it lands, weigh it on camera,
        and have your offer the same day it arrives. Or fill in the front and post it.
        Both work.
      </p>

      <p className="card-back-url">{run.url}</p>
      <p className="card-back-help">Questions? {company.phone}</p>
    </article>
  );
}

////////// 4. THE SCREEN //////////

export default function Cards() {
  const [params] = useSearchParams();

  // `@page` cannot be scoped to a class and this route prints something a
  // different shape from the label sheets, so the rule for it is written here
  // and taken away again on the way out. Letter with no margin, because the
  // sheet is exactly Letter and the quarters have their own padding.
  useEffect(() => {
    const rule = document.createElement("style");
    rule.textContent = "@page { size: letter portrait; margin: 0; }";
    document.head.appendChild(rule);
    return () => rule.remove();
  }, []);

  const code = params.get("code") || "";
  const [zoom, setZoom] = useState(false);

  const { data: run, loading, error } = useAsync(
    (signal) => affiliatesApi.cards(code, signal),
    [code],
    { skip: !code }
  );

  if (!code) {
    return (
      <div className="p-4 sm:p-6 lg:p-8">
        <Alert tone="error" title="No affiliate chosen">Open this from an affiliate's page.</Alert>
      </div>
    );
  }
  if (loading) return <Loading label="Building the card" className="min-h-screen" />;
  if (error) {
    return <div className="p-4 sm:p-6 lg:p-8"><Alert tone="error" title="Could not build it">{error.message}</Alert></div>;
  }

  const sheet = Array.from({ length: run.perSheet }, (_, index) => index);
  const fronts = <>{sheet.map((i) => <CardFront key={i} company={run.company} coverage={run.coverage} />)}</>;
  const backs = <>{sheet.map((i) => <CardBack key={i} run={run} company={run.company} />)}</>;

  return (
    <div className="cards-page">
      <div className="no-print p-4 sm:p-6 lg:p-8 max-w-[900px] space-y-5">
        <Link to="/admin/affiliates" className="inline-flex items-center gap-1.5 text-[13px] text-muted hover:text-ink">
          <ArrowLeft size={15} /> Affiliates
        </Link>

        <Panel className="p-5 space-y-4">
          <div>
            <p className="eyebrow">Counter cards for {run.affiliate?.name}</p>
            <p className="hint mt-1 leading-relaxed">
              One goes in each display mailer. Two pages, four cards to a page: the form on page one,
              the QR code on page two. Every card is the same, so there is nothing to line up and
              nothing to keep in order.
            </p>
          </div>

          {/* The settings that matter are in the browser's own dialog, so this
              names them rather than trying to own them. Scale is the one that
              quietly ruins a run: "fit to page" shrinks a 4.25 inch card to
              about 4.1 and every cut is then in the wrong place. */}
          <Alert tone="info">
            In the print dialog: <b>Two-sided</b> on, <b>Scale 100%</b> rather than fit to page, and
            <b> Margins: None</b>. Set <b>Copies</b> to however many sheets you want. Then cut each
            sheet into quarters along the dotted lines.
          </Alert>

          <div className="flex flex-wrap gap-2">
            <Button onClick={() => window.print()}><Printer size={14} /> Print</Button>
            <Button variant="quiet" onClick={() => setZoom((on) => !on)}>
              {zoom ? "Shrink the preview" : "Show it at print size"}
            </Button>
          </div>

          <p className="hint leading-relaxed">
            The QR code goes to <span className="num">{run.url}</span>, which credits the order to{" "}
            {run.affiliate?.code} and knows the customer already has a mailer, so nothing is posted
            to them.
          </p>
        </Panel>

        {/* Screen only, and scaled down by default so a whole sheet is visible
            at once. Print size is a click away for reading the small print. */}
        <div className={zoom ? "cards-preview cards-preview-full" : "cards-preview"}>
          <section className="card-sheet">{fronts}</section>
          <section className="card-sheet">{backs}</section>
        </div>
      </div>

      {/* What actually prints: two pages, in this order, and nothing else. The
          panel above carries `no-print`, and the preview is inside it. */}
      <div className="print-only">
        <section className="card-sheet">{fronts}</section>
        <section className="card-sheet">{backs}</section>
      </div>
    </div>
  );
}
